import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Smart API URL switching
const API_BASE_URL =
    window.location.hostname === "staging187611.digassistant.xyz"
        ? "https://api.digassistant.xyz"
        : window.location.hostname === "digassistant.xyz" || window.location.hostname === "www.digassistant.xyz"
            ? "https://api.digassistant.xyz"
            : "http://localhost:8080";

const VerificationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [email, setEmail] = useState(location.state?.email || null);
    const [videoFailed, setVideoFailed] = useState(false);
    const inputRefs = useRef([]);
    const cooldownIntervalRef = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || videoFailed) return;
        const playPromise = video.play();
        if (playPromise !== undefined) {
            playPromise.catch(() => setVideoFailed(true));
        }
    }, [videoFailed]);

    const getEmailFromJWT = () => {
        try {
            const cookies = document.cookie.split(';');
            const sessionCookie = cookies.find(cookie => cookie.trim().startsWith('session='));
            if (sessionCookie) {
                const token = sessionCookie.split('=')[1];
                const payload = token.split('.')[1];
                if (payload) {
                    const decoded = JSON.parse(atob(payload));
                    return decoded.email;
                }
            }
        } catch (error) {
            console.error('Error decoding JWT:', error);
        }
        return null;
    };

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/auth/check`, {
                    credentials: "include",
                });

                const data = await res.json();

                if (data.authenticated && data.verified !== false) {
                    if (data.isPremium) {
                        navigate('/premium');
                    } else {
                        navigate('/service');
                    }
                    return;
                }

                if (data.verified === false) {
                    if (!email) {
                        const jwtEmail = getEmailFromJWT();
                        if (jwtEmail) {
                            setEmail(jwtEmail);
                        } else {
                            console.error('Could not get email from JWT, redirecting to login');
                            navigate('/login');
                        }
                    }
                    return;
                }
            } catch (error) {
                console.error('Auth check failed:', error);
            }

            if (!email) {
                navigate('/login');
            }
        };

        checkAuth();
    }, [email, navigate]);

    useEffect(() => {
        return () => {
            if (cooldownIntervalRef.current) {
                clearInterval(cooldownIntervalRef.current);
                cooldownIntervalRef.current = null;
            }
        };
    }, []);

    const handleInputChange = (index, value) => {
        if (value.length > 1) return;

        const newCode = [...verificationCode];
        newCode[index] = value;
        setVerificationCode(newCode);
        setError('');

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        if (value && index === 5) {
            const completeCode = newCode.join('');
            handleVerificationWithCode(completeCode);
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');

        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            const newCode = [...verificationCode];

            for (let i = 0; i < 6; i++) {
                newCode[i] = digits[i] || '';
            }

            setVerificationCode(newCode);
            setError('');
            inputRefs.current[5]?.focus();
            handleVerificationWithCode(pastedData);
        }
    };

    const handleVerificationWithCode = async (code) => {
        if (code.length !== 6) {
            setError('Please enter all 6 digits');
            return;
        }

        setIsProcessing(true);
        setError('');

        try {
            const res = await fetch(`${API_BASE_URL}/auth/manual/user/verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: email,
                    code: code
                }),
                credentials: "include",
            });

            if (res.ok) {
                navigate("/service");
            } else {
                let errorMessage = "Verification failed. Please try again.";
                try {
                    const errorData = await res.json();
                    errorMessage = errorData.error || errorMessage;
                } catch (e) {
                    const errorText = await res.text();
                    errorMessage = errorText || errorMessage;
                }
                console.error("❌ Verification failed:", errorMessage);
                setError(errorMessage);
                setIsProcessing(false);
            }
        } catch (error) {
            console.error("❌ Verification error:", error);
            setError("Network error. Please try again.");
            setIsProcessing(false);
        }
    };

    const handleVerification = async () => {
        const code = verificationCode.join('');
        handleVerificationWithCode(code);
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0) return;

        if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
        }

        try {
            const res = await fetch(`${API_BASE_URL}/auth/manual/user/resendcode`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: email
                }),
                credentials: "include",
            });

            if (res.ok) {
                setResendCooldown(60);
                cooldownIntervalRef.current = setInterval(() => {
                    setResendCooldown(prev => {
                        const nextValue = prev - 1;
                        if (nextValue <= 0) {
                            if (cooldownIntervalRef.current) {
                                clearInterval(cooldownIntervalRef.current);
                                cooldownIntervalRef.current = null;
                            }
                            return 0;
                        }
                        return nextValue;
                    });
                }, 1000);
            } else {
                console.error("❌ Resend code failed");
                setError("Failed to resend code. Please try again.");
            }
        } catch (error) {
            console.error("❌ Resend code error:", error);
            setError("Network error. Please try again.");
        }
    };

    const videoBackground = (
        <>
            {!videoFailed && (
                <video ref={videoRef} autoPlay loop muted playsInline preload="auto" style={{
                    position: 'absolute', top: '50%', left: '50%',
                    minWidth: '100%', minHeight: '100%', width: 'auto', height: 'auto',
                    transform: 'translate(-50%, -50%) scale(0.45)', objectFit: 'cover', zIndex: 0
                }}>
                    <source src="https://storage.googleapis.com/tmhimagebucket/clip2compdouble.mp4" type="video/mp4" />
                </video>
            )}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1 }} />
        </>
    );

    const bgStyle = { minHeight: '100vh', position: 'relative', overflow: 'hidden', ...(videoFailed && { backgroundImage: 'url("https://storage.googleapis.com/tmhimagebucket/lathedubplate.jpg")', backgroundSize: 'cover', backgroundPosition: 'center' }) };

    if (isProcessing) {
        return (
            <div style={bgStyle}>
                {videoBackground}
                <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', textAlign: 'center' }}>
                    <div style={{ color: '#009e4a' }}>
                        <h1>Verifying your email...</h1>
                        <p>Please wait while we verify your code.</p>
                    </div>
                </div>
            </div>
        );
    }

    const isMobile = window.innerWidth <= 600;

    return (
        <div style={bgStyle}>
            {videoBackground}
            <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', textAlign: 'center', cursor: 'default' }}>
            <div style={{ width: "100%", maxWidth: "400px", padding: isMobile ? "0 15px" : "0 20px", boxSizing: "border-box" }}>
                <p style={{
                    color: '#009e4a',
                    marginBottom: '10px',
                    fontFamily: 'CustomFont',
                    cursor: "default",
                    fontSize: "clamp(14px, 4vw, 16px)",
                    lineHeight: "1.5",
                    wordBreak: "break-word"
                }}>
                    Enter the 6 digit code sent to {email}
                </p>
                <p style={{
                    color: 'rgba(0, 158, 74, 0.7)',
                    marginBottom: '30px',
                    fontFamily: 'CustomFont',
                    cursor: "default",
                    fontSize: "12px",
                    lineHeight: "1.4"
                }}>
                    Please check your spam folder
                </p>

                {error && (
                    <div style={{
                        color: "red",
                        fontSize: "12px",
                        marginBottom: "20px",
                        fontFamily: 'CustomFont',
                        cursor: "default"
                    }}>
                        {error}
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: isMobile ? '5px' : 'clamp(8px, 2vw, 10px)',
                    marginBottom: '30px',
                    flexWrap: 'nowrap',
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    {verificationCode.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength="1"
                            value={digit}
                            onChange={(e) => handleInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            style={{
                                width: 'clamp(40px, 12vw, 50px)',
                                minWidth: '40px',
                                maxWidth: '50px',
                                height: 'clamp(50px, 12vw, 50px)',
                                minHeight: '50px',
                                maxHeight: '50px',
                                textAlign: 'center',
                                fontSize: 'clamp(20px, 5vw, 24px)',
                                fontFamily: 'CustomFont',
                                border: '1px solid #009e4a',
                                borderRadius: '5px',
                                backgroundColor: 'rgba(0,0,0,0.4)',
                                color: '#009e4a',
                                outline: 'none',
                                cursor: 'text',
                                boxSizing: 'border-box',
                                flexShrink: 0
                            }}
                        />
                    ))}
                </div>

                <button
                    onClick={handleVerification}
                    disabled={verificationCode.join('').length !== 6 || isProcessing}
                    style={{
                        background: 'none',
                        border: '1px solid #009e4a',
                        color: verificationCode.join('').length !== 6 ? 'rgba(0, 158, 74, 0.5)' : '#009e4a',
                        padding: '14px 24px',
                        borderRadius: '5px',
                        cursor: verificationCode.join('').length !== 6 ? 'not-allowed' : 'pointer',
                        fontFamily: 'CustomFont',
                        fontSize: '16px',
                        transition: 'all 0.2s',
                        marginBottom: '20px',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                    onMouseOver={(e) => {
                        if (verificationCode.join('').length === 6) {
                            e.target.style.background = '#009e4a';
                            e.target.style.color = 'black';
                        }
                    }}
                    onMouseOut={(e) => {
                        if (verificationCode.join('').length === 6) {
                            e.target.style.background = 'none';
                            e.target.style.color = '#009e4a';
                        }
                    }}
                >
                    Verify Code
                </button>

                <div style={{ marginTop: '20px' }}>
                    <button
                        key={`resend-${resendCooldown}`}
                        onClick={handleResendCode}
                        disabled={resendCooldown > 0}
                        style={{
                            background: resendCooldown > 0 ? 'rgba(0, 158, 74, 0.1)' : 'none',
                            border: '1px solid #009e4a',
                            color: resendCooldown > 0 ? 'rgba(0, 158, 74, 0.7)' : '#009e4a',
                            padding: '14px 24px',
                            borderRadius: '5px',
                            cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                            fontFamily: 'CustomFont',
                            fontSize: '16px',
                            transition: 'all 0.2s',
                            width: '100%',
                            boxSizing: 'border-box',
                            minHeight: '44px',
                            whiteSpace: 'nowrap',
                            overflow: 'visible',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onMouseOver={(e) => {
                            if (resendCooldown === 0 && !e.target.disabled) {
                                e.target.style.background = '#009e4a';
                                e.target.style.color = 'black';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (resendCooldown === 0 && !e.target.disabled) {
                                e.target.style.background = 'none';
                                e.target.style.color = '#009e4a';
                            }
                        }}
                    >
                        <span style={{ display: 'inline-block' }}>
                            {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
                        </span>
                    </button>
                </div>
            </div>
            </div>
        </div>
    );
};

export default VerificationPage;
